import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { GameViewProps } from '../../core/contracts'
import { haptics } from '../../platform/telegram'
import { useT } from '../../platform/i18n/useT'
import { SECTORS } from './logic'
import type { Arm, SnowflakeAnswer, SnowflakeRound } from './logic'
import './view.css'

/**
 * Экран Snowflake.
 *
 * Снежинка строится из шести повторов одного луча. Один сектор пустой -
 * игрок выбирает подходящий луч из вариантов.
 *
 * Вся геометрия считается здесь: логика игры оперирует параметрами
 * (положение, длина, угол веток), а не координатами. Так параметры
 * остаются тестируемыми, а отрисовка - заменяемой.
 */

type Segment = readonly [number, number, number, number]

/** Отрезки одного луча. Начало координат - центр, луч смотрит вверх. */
function armSegments(arm: Arm, R: number): Segment[] {
  const out: Segment[] = [[0, 0, 0, -R]]

  for (const b of arm.branches) {
    const py = -R * b.at
    const L = R * b.len
    for (const side of [-1, 1]) {
      const a = (b.angle * Math.PI) / 180
      out.push([0, py, Math.sin(a) * L * side, py - Math.cos(a) * L])
    }
  }

  if (arm.tip === 'fork') {
    const L = R * 0.18
    for (const side of [-1, 1]) {
      const a = (38 * Math.PI) / 180
      out.push([0, -R, Math.sin(a) * L * side, -R - Math.cos(a) * L])
    }
  } else if (arm.tip === 'diamond') {
    const k = R * 0.09
    out.push([0, -R + k, k, -R], [k, -R, 0, -R - k], [0, -R - k, -k, -R], [-k, -R, 0, -R + k])
  }

  return out
}

function Flake({ arm, missing, size }: { arm: Arm; missing: number; size: number }) {
  const R = size * 0.42
  const segments = useMemo(() => armSegments(arm, R), [arm, R])

  return (
    <svg viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`} className="sf__svg">
      {Array.from({ length: SECTORS }, (_, s) =>
        s === missing ? null : (
          <g key={s} transform={`rotate(${(360 / SECTORS) * s})`}>
            {segments.map(([x1, y1, x2, y2], i) => (
              <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
            ))}
          </g>
        ),
      )}
      {/* пустой сектор помечаем пунктиром - иначе непонятно, куда смотреть */}
      <g transform={`rotate(${(360 / SECTORS) * missing})`} className="sf__gap">
        <line x1={0} y1={0} x2={0} y2={-R} />
      </g>
    </svg>
  )
}

function ArmPreview({ arm, size }: { arm: Arm; size: number }) {
  const R = size * 0.38
  const segments = useMemo(() => armSegments(arm, R), [arm, R])
  return (
    <svg viewBox={`${-size / 2} ${-size * 0.62} ${size} ${size * 0.72}`} className="sf__svg">
      {segments.map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
      ))}
    </svg>
  )
}

export function SnowflakeView({
  round,
  level,
  onAnswer,
}: GameViewProps<SnowflakeRound, SnowflakeAnswer>) {
  const { t } = useT()
  const [index, setIndex] = useState(0)
  const [flash, setFlash] = useState<'hit' | 'miss' | null>(null)

  const picks = useRef<(number | null)[]>([])
  const times = useRef<number[]>([])
  const shownAt = useRef(performance.now())
  const locked = useRef(false)

  const trial = round.trials[index]

  const finish = useCallback(() => {
    const ok = times.current
    const rtMs =
      ok.length > 0
        ? Math.round(ok.reduce((a, b) => a + b, 0) / ok.length)
        : round.timeLimitMs
    onAnswer({ picks: picks.current, rtMs })
  }, [onAnswer, round.timeLimitMs])

  const submit = useCallback(
    (pick: number | null) => {
      if (locked.current || !trial) return
      locked.current = true

      const rt = performance.now() - shownAt.current
      const hit = pick === trial.correctIndex

      picks.current.push(pick)
      if (hit) times.current.push(rt)

      if (hit) haptics.correct()
      else haptics.wrong()
      setFlash(hit ? 'hit' : 'miss')

      setTimeout(() => {
        setFlash(null)
        locked.current = false
        if (index + 1 >= round.trials.length) {
          finish()
        } else {
          shownAt.current = performance.now()
          setIndex((i) => i + 1)
        }
      }, 260)
    },
    [trial, index, round.trials.length, finish],
  )

  useEffect(() => {
    shownAt.current = performance.now()
    const id = setTimeout(() => submit(null), round.timeLimitMs)
    return () => clearTimeout(id)
  }, [index, round.timeLimitMs, submit])

  if (!trial) return null

  return (
    <div className="sf">
      <div className="sf__top">
        <span>{t('game.level', { n: level })}</span>
        <span>
          {index + 1} / {round.trials.length}
        </span>
      </div>

      <div className="sf__timer" aria-hidden="true">
        <div
          key={index}
          className="sf__timer-fill"
          style={{ animationDuration: `${round.timeLimitMs}ms` }}
        />
      </div>

      <p className="sf__hint">{t('game.snowflake.hint')}</p>

      <div className={flash ? `sf__stage sf__stage--${flash}` : 'sf__stage'}>
        <Flake arm={trial.arm} missing={trial.missingSector} size={260} />
      </div>

      <div className="sf__options">
        {trial.options.map((arm, i) => (
          <button
            key={i}
            type="button"
            className="sf__opt"
            onClick={() => submit(i)}
            aria-label={`${i + 1}`}
          >
            <ArmPreview arm={arm} size={120} />
          </button>
        ))}
      </div>
    </div>
  )
}
